import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card } from '../ui/card';
import { Award, Zap, Info, ChevronDown, ChevronUp } from 'lucide-react';
import InfoTooltip from '../InfoTooltip';

// Local palette — matches ClinicSnapshotPanel + BI Bonus card
const C = {
  ink: '#2D2833',
  soft: '#685B72',
  border: '#E8E5EC',
  hair: '#B08A3A',
  gold: '#B08A3A',
  teal: '#839E97',
  rose: '#8A3A3A',
  amber: '#B08A3A',
  paper: '#FBF8F4',
};

const NUM = (n) => (n == null ? '—' : Number(n).toLocaleString());

export default function ClinicBonusPanel({ clinicId, clinicName }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const storageKey = clinicId ? `clinic-bonus-collapsed:${clinicId}` : null;
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined' || !clinicId) return false;
    try { return window.localStorage.getItem(`clinic-bonus-collapsed:${clinicId}`) === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    if (!clinicId) return;
    api.get(`/clinics/${clinicId}/bonus`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Bonus data unavailable'));
  }, [clinicId]);

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try { window.localStorage.setItem(storageKey, collapsed ? '1' : '0'); } catch {}
  }, [collapsed, storageKey]);

  if (err) return null;                              // no BI data yet — hide silently
  if (!data) return null;                            // still loading
  const row = data.row;
  const fullTarget = data.target_per_clinic;         // full quarter (2,225)
  const paceTarget = data.elapsed_target_per_clinic || fullTarget;
  const bdElapsed = data.business_days_elapsed;
  const bdTotal = data.business_days_total;
  const qualPct = data.qualifying_pct;
  if (!row || !fullTarget || qualPct == null) return null;  // BI cube empty for this clinic

  // Iter 242 — the primary "how are we doing" number is business-day-
  // prorated pace progress: shows / elapsed_target. Full-quarter
  // progress (row.progress) is still surfaced below as the eligibility
  // reference.
  const paceProgress = (row.pace_progress != null) ? Number(row.pace_progress) : (paceTarget ? (row.shows || 0) / paceTarget : 0);
  const fullProgress = Number(row.progress || 0);
  const pctDisplay = Math.round(paceProgress * 100);
  const paceQualifyingSessions = data.pace_qualifying_sessions ?? Math.round(paceTarget * qualPct);
  const qualifyingSessions = Math.round(fullTarget * qualPct);
  const remaining = Math.max(0, paceTarget - (row.shows || 0));
  const barPct = Math.min(100, paceProgress * 100);
  const barColor = paceProgress >= 1.0 ? C.teal : paceProgress >= 0.90 ? C.amber : C.rose;
  const qualifiesLabel = paceProgress >= 1.0
    ? 'On pace for bonus'
    : paceProgress >= 0.90
      ? 'Close to pace'
      : 'Behind pace';

  return (
    <Card
      className="rounded-2xl border p-6 bg-white"
      style={{ borderColor: C.border }}
      data-testid="clinic-bonus-panel"
    >
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: C.gold }}>
            <Award size={12} strokeWidth={2.4} /> OD Quarterly Bonus · {data.label}
          </div>
          <h3 className="font-serif text-2xl mt-1 flex items-center gap-2" style={{ color: C.ink }}>
            <span>{clinicName} — bonus tracker</span>
            <InfoTooltip testId="tt-bonus-tracker" text="Tracks visits toward the OD quarterly bonus. 'Counted' visits = Show + No Show + Late Cancel (per Thrive's bonus formula). Bonus qualifies when your quarter total reaches the target (~90% of capacity)." />
            <button
              type="button"
              onClick={() => setCollapsed(v => !v)}
              className="ml-1 inline-flex items-center justify-center rounded-full border transition-colors"
              style={{ borderColor: C.border, background: C.paper, width: 28, height: 28, color: C.soft }}
              aria-label={collapsed ? 'Expand bonus tracker' : 'Collapse bonus tracker'}
              aria-expanded={!collapsed}
              data-testid="clinic-bonus-toggle"
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? <ChevronDown size={16} strokeWidth={2.4} /> : <ChevronUp size={16} strokeWidth={2.4} />}
            </button>
          </h3>
          {collapsed ? (
            <div
              className="text-[13px] mt-1 tabular-nums"
              style={{ color: C.soft }}
              data-testid="clinic-bonus-collapsed-summary"
            >
              <span style={{ color: C.ink, fontWeight: 700 }}>{NUM(row.shows)}</span>
              {' / '}
              <span style={{ color: C.ink, fontWeight: 700 }}>{NUM(paceTarget)}</span>
              {' · '}
              <span style={{ color: barColor, fontWeight: 700 }}>{pctDisplay}%</span>
              <span className="ml-2" style={{ color: C.soft }}>· {qualifiesLabel}</span>
              {bdElapsed != null && bdTotal ? <span className="ml-2" style={{ color: C.soft }}>· {bdElapsed}/{bdTotal} biz days</span> : null}
            </div>
          ) : (
            <div className="text-[12px] mt-1" style={{ color: C.soft }}>
              Pace target <span style={{ color: C.ink2, fontWeight: 700 }}>{NUM(paceTarget)}</span>
              {bdElapsed != null && bdTotal ? <> ({bdElapsed}/{bdTotal} biz days elapsed of {NUM(fullTarget)} full-quarter)</> : null}
              {' · '}
              {(data.months || []).join(' · ')} · <span title="Show + No Show + Late Canceled">counted: shows + no-shows + late cancels</span>
            </div>
          )}
        </div>
        {!collapsed && (
          <div className="text-right">
            <div className="font-serif tabular-nums leading-none" style={{ color: barColor, fontSize: 40 }}>
              {pctDisplay}<span className="text-[20px]" style={{ color: C.soft }}>%</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: C.soft }}>
              {qualifiesLabel}
            </div>
          </div>
        )}
      </div>

      {!collapsed && (<>
      {/* Progress bar with pace-qualifying threshold marker */}
      <div className="relative h-4 rounded-full overflow-hidden mb-2" style={{ background: '#F5F1EA', border: `1px solid ${C.border}` }} data-testid="clinic-bonus-bar">
        {/* 90%-of-pace qualifying line */}
        <div
          className="absolute top-0 bottom-0 z-10"
          style={{ left: `${qualPct * 100}%`, width: 2, background: C.gold, opacity: 0.85 }}
          data-testid="clinic-bonus-threshold-line"
        />
        {/* Fill */}
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${barPct}%`, background: barColor }}
        />
      </div>
      <div className="flex justify-between text-[10px] uppercase tracking-widest mb-4" style={{ color: C.soft }}>
        <span>0</span>
        <span data-testid="clinic-bonus-threshold-label" className="flex items-center gap-1" style={{ color: C.gold, fontWeight: 700 }}>
          <Zap size={10} strokeWidth={2.4} /> 90% of pace · {NUM(paceQualifyingSessions)}
        </span>
        <span>{NUM(paceTarget)}</span>
      </div>

      {/* Metric tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-xl border p-3" style={{ borderColor: C.border, background: C.paper }}>
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>Sessions to date</div>
          <div className="font-serif text-2xl mt-1 tabular-nums" style={{ color: C.ink }} data-testid="clinic-bonus-shows">{NUM(row.shows)}</div>
        </div>
        <div className="rounded-xl border p-3" style={{ borderColor: C.border, background: C.paper }}>
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>Pace target ({bdElapsed ?? '—'}/{bdTotal ?? '—'} bd)</div>
          <div className="font-serif text-2xl mt-1 tabular-nums" style={{ color: C.ink }} data-testid="clinic-bonus-pace-target">{NUM(paceTarget)}</div>
        </div>
        <div className="rounded-xl border p-3" style={{ borderColor: C.border, background: C.paper }}>
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>Full-quarter target</div>
          <div className="font-serif text-2xl mt-1 tabular-nums" style={{ color: C.ink }}>{NUM(fullTarget)}</div>
        </div>
        <div className="rounded-xl border p-3" style={{ borderColor: C.border, background: C.paper }}>
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>Prior year (same Q)</div>
          <div className="font-serif text-2xl mt-1 tabular-nums" style={{ color: C.ink }} data-testid="clinic-bonus-py">
            {row.prior_year_shows ? NUM(row.prior_year_shows) : '—'}
          </div>
        </div>
      </div>

      {/* Eligibility note */}
      <div
        className="flex items-start gap-2 rounded-xl p-3 text-[12px]"
        style={{ background: `${C.gold}15`, border: `1px solid ${C.gold}55`, color: C.ink }}
        data-testid="clinic-bonus-eligibility-note"
      >
        <Info size={14} strokeWidth={2.4} style={{ color: C.gold, marginTop: 2 }} />
        <span>
          <span style={{ fontWeight: 700, color: C.gold }}>Pace vs eligibility:</span>{' '}
          Current pace <span style={{ fontWeight: 700 }}>{pctDisplay}%</span> against <span style={{ fontWeight: 700 }}>{NUM(paceTarget)}</span> pace target. Final bonus needs <span style={{ fontWeight: 700 }}>{NUM(qualifyingSessions)}</span> sessions ({(qualPct*100).toFixed(0)}% of full-quarter {NUM(fullTarget)}). Quarter-to-date progress: <span style={{ fontWeight: 700 }}>{(fullProgress*100).toFixed(1)}%</span> of full target.
          {paceProgress < 1.0 && (
            <> Need <span style={{ fontWeight: 700 }}>{NUM(remaining)}</span> more session{remaining === 1 ? '' : 's'} to catch up to today's pace.</>
          )}
          {data.office_director && (
            <> Office Director: <span style={{ fontWeight: 700 }}>{data.office_director}</span>.</>
          )}
        </span>
      </div>

      {/* Formula breakdown — matches Quarterly Bonus Calculator workbook */}
      <div className="mt-3 rounded-xl border p-3 text-[11px]" style={{ borderColor: C.border, background: C.paper }} data-testid="clinic-bonus-formula">
        <div className="uppercase tracking-widest font-semibold mb-1" style={{ color: C.soft }}>How this number is built</div>
        <div style={{ color: C.ink }}>
          <span style={{ fontWeight: 700 }}>Sessions</span> = Team Member Sessions + OD Therapy Sessions + OD Evaluations (92523)
          &nbsp;·&nbsp; counted statuses: Show + No Show + Late Canceled
          &nbsp;·&nbsp; Video Office sessions are re-credited to each clinician's home clinic
          &nbsp;·&nbsp; "Thrive Speech Therapy PLLC" is excluded
        </div>
      </div>

      {/* Bonus history — last 4 quarters */}
      {Array.isArray(data.history) && data.history.length > 0 && (
        <div className="mt-5" data-testid="clinic-bonus-history">
          <div className="text-[10px] uppercase tracking-widest font-semibold mb-2" style={{ color: C.soft }}>
            Last 4 quarters
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.history.map(q => {
              const qrow = q.row;
              const qshows = qrow?.shows ?? 0;
              const qtarget = q.target_per_clinic || 1;
              const qpct = qtarget ? qshows / qtarget : 0;
              const qBar = Math.min(100, qpct * 100);
              const qColor = qrow?.qualifies ? C.teal : qpct >= 0.75 ? C.amber : C.rose;
              const qBg = q.is_current ? `${C.gold}12` : C.paper;
              const qBorder = q.is_current ? `${C.gold}66` : C.border;
              return (
                <div
                  key={q.label}
                  className="rounded-xl border p-3"
                  style={{ borderColor: qBorder, background: qBg }}
                  data-testid={`clinic-bonus-history-${(q.label || '').replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="text-[11px] font-bold tracking-widest" style={{ color: q.is_current ? C.gold : C.ink }}>
                      {q.label}{q.is_current ? ' · Current' : ''}
                    </div>
                    <div className="text-[10px]" style={{ color: qrow?.qualifies ? C.teal : C.soft, fontWeight: 700 }}>
                      {qrow?.qualifies ? 'Bonus ✓' : 'Missed'}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <div className="font-serif text-lg tabular-nums" style={{ color: C.ink }}>
                      {NUM(qshows)}
                    </div>
                    <div className="text-[10px]" style={{ color: C.soft }}>
                      / {NUM(qtarget)}
                    </div>
                    <div className="ml-auto text-[11px] tabular-nums" style={{ color: qColor, fontWeight: 700 }}>
                      {Math.round(qpct * 100)}%
                    </div>
                  </div>
                  <div className="relative h-2 rounded-full overflow-hidden mt-2" style={{ background: C.hair }}>
                    <div
                      className="absolute top-0 bottom-0 z-10"
                      style={{ left: `${(q.qualifying_pct || 0.9) * 100}%`, width: 1, background: C.gold, opacity: 0.75 }}
                    />
                    <div className="h-full" style={{ width: `${qBar}%`, background: qColor }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>)}
    </Card>
  );
}
